import type { ComponentProps, PropsWithChildren } from "react";

import { CheckCircle } from "@similarweb/icons";
import { CircularLoader, Tooltip } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import {
    COLOR_BY_CHIP_TYPE,
    SMALL_LOADER_OPTIONS,
    StyledCircularLoaderWrapper,
    StyledManagementCardChip,
} from "./styles";
import type { CardHeaderChipType } from "./types";

type IconProps = ComponentProps<typeof CheckCircle>;

export const CHIP_ICON_BY_CHIP_TYPE: Partial<Record<CardHeaderChipType, React.FC<IconProps>>> = {
    success: CheckCircle,
};

export interface IManagementCardChipProps {
    chipType?: CardHeaderChipType;
    isLoading?: boolean;
    tooltipText?: string | string[];
}

const ManagementCardChip = ({
    chipType = "success",
    isLoading,
    tooltipText,
    children,
}: PropsWithChildren<IManagementCardChipProps>) => {
    const IconComponent = CHIP_ICON_BY_CHIP_TYPE[chipType];
    const tooltipItems: string[] = !tooltipText ? [] : Array.isArray(tooltipText) ? tooltipText : [tooltipText];
    const isTooltipEnabled = !!tooltipItems.length;

    const tooltipContent = (
        <Flex
            flexDirection="column"
            gap="8px"
        >
            {tooltipItems.map((text, index) => (
                <div key={index}>{text}</div>
            ))}
        </Flex>
    );

    return (
        <Tooltip
            enabled={isTooltipEnabled}
            content={tooltipContent}
        >
            <StyledManagementCardChip type={chipType}>
                {IconComponent && !isLoading ? (
                    <IconComponent
                        size="xs"
                        color={COLOR_BY_CHIP_TYPE[chipType]}
                    />
                ) : null}

                {isLoading ? (
                    <StyledCircularLoaderWrapper type={chipType}>
                        <CircularLoader options={SMALL_LOADER_OPTIONS} />
                    </StyledCircularLoaderWrapper>
                ) : null}

                <Text
                    fontVariant="listItem3"
                    color={COLOR_BY_CHIP_TYPE[chipType]}
                >
                    {children}
                </Text>
            </StyledManagementCardChip>
        </Tooltip>
    );
};

export default ManagementCardChip;
